import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PageLayoutService } from '../../../../../../services/menu/page-layout.service';
import { Breadcrumb } from '../../../../../breadcrumbs/breadcrumb';
import { BreadcrumbsService } from '../../../../../breadcrumbs/breadcrumbs.service';
import { View360itemComponent } from '../../view360/view360item/view360item.component';
import { MenuActionDirective } from '../page-base/directives/menu-action.directive';

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  animations: [
    trigger('animationDropDown', [
      transition(':enter', [
        style({
          opacity: 0,
          top: '-90px',
          height: 0,
        }),
        animate(
          '.4s cubic-bezier(.32,.9,.9,.96)',
          style({
            opacity: 1,
            top: '0',
            height: '*',
          }),
        ),
      ]),
      transition(':leave', [
        style({
          height: '*',
          opacity: 1,
          top: 0,
        }),
        animate(
          '.3s cubic-bezier(.32,.9,.9,.96)',
          style({
            height: 0,
            opacity: 0,
            top: '-90px',
          }),
        ),
      ]),
    ]),
  ],
})
export class PageComponent implements OnInit, OnDestroy {
  menu360Selected: View360itemComponent;

  menuActions: any[];
  private currentBreadCrumb: Breadcrumb;
  private subs: Subscription[] = [];
  private _titre: string;
  private _domaine: string;

  @Input() set titre(value: string) {
    this._titre = value;
    this.updateTitle();
  }

  @Input() sousTitre: string;
  @Input() set domaine(value: string) {
    this._domaine = value;
    this.updateTitle();
  }

  @Input() chips: TemplateRef<any>;
  @Input() icon: TemplateRef<any>;
  @Input() enteteSimple = false;
  @Input() miseEnAvant = false;
  @Input() miseEnAvantDisabled = false;
  @Input() hideRetour = false;
  @Input() afficherRetourPersonnalise = false;
  @Input() boutonRetourSansFondBlanc = false;
  @Output() retourPersonnalise = new EventEmitter<void>();

  get sortedMenus(): View360itemComponent[] {
    return this.pageLayoutService.pages.sort((left, right) => left.ordre - right.ordre)
  }
  get isHeaderVisible() {
    if (this.menu360Selected != null) {
      return !this.menu360Selected.hideEntete;
    } else {
      return true;
    }
  }

  get domaine() {
    return this._domaine;
  }
  get titre() {
    return this._titre;
  }

  private updateTitle() {
    let titrePage = this._titre;
    if (this.getMenuSelectedCaption != null && this.getMenuSelectedCaption != '') {
      titrePage = `${titrePage} - ${this.getMenuSelectedCaption}`;
    }
    this.titleService.setTitle(titrePage);
  }

  private currentPageIsSubpage() {
    return this.pageLayoutService.pageSelected != null && this.pageLayoutService.pageSelected.parent != null;
  }

  get selectedMenuTitle() {
    if (this.menu360Selected != null) {
      return this.menu360Selected.caption;
    } else {
      return this.titre;
    }
  }

  get getHideRetour() {
    return this.hideRetour;
  }

  constructor(
    public pageLayoutService: PageLayoutService,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private crumbService: BreadcrumbsService,
    private router: Router,
  ) {
    this.subs.push(
      pageLayoutService.menuActif$.subscribe(page => {
        this.menu360Selected = page;
        this.updateTitle();
        if (this.currentBreadCrumb != null) {
          this.crumbService.notifyNavigation(this.currentBreadCrumb, this.router.url);
        }
      }),
    );
    this.menu360Selected = this.pageLayoutService.pageSelected;
    this.subs.push(
      this.pageLayoutService.menuActionsChanged$.subscribe(menuActions => {
        this.buildMenuActions(menuActions);
      }),
    );
    this.subs.push(
      this.activatedRoute.data.subscribe(data => {
        if (data.breadcrumb != null) {
          this.currentBreadCrumb = data.breadcrumb;
          this.crumbService.notifyNavigation(data.breadcrumb, this.router.url);
        } else {
          this.crumbService.notifyNavigation(Breadcrumb.empty(), this.router.url);
        }
      }),
    );
  }

  private buildMenuActions(actions: MenuActionDirective[]) {
    this.menuActions = actions.map((action, idx) => {
      return {
        id: idx,
        text: action.caption,
        action: action.action,
      };
    });
  }

  get hasMenu360() {
    return this.pageLayoutService.pages.length > 0;
  }

  public actionMenu360item(item: View360itemComponent, event?: MouseEvent) {
    event?.preventDefault();
    if (item.action) {
      item.action.emit();
    }
  }

  private isSubmenuSelected(page: View360itemComponent) {
    return (
      this.pageLayoutService.pageSelected != null &&
      this.pageLayoutService.pageSelected.parent != null &&
      this.pageLayoutService.pageSelected.parent == page.caption
    );
  }

  public isMenuSelected(page: View360itemComponent) {
    return page.path == this.pageLayoutService.pathSelected || this.isSubmenuSelected(page);
  }

  private get getMenuSelectedCaption() {
    const selected = this.pageLayoutService.pages.find(it => this.isMenuSelected(it));
    if (selected != null) {
      return selected.caption;
    } else {
      return ``;
    }
  }

  public ngOnInit() {}

  private unsubscribeAll() {
    this.subs.forEach(it => it.unsubscribe());
    this.subs = [];
  }

  public ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  public isMenu360Visible(page: View360itemComponent) {
    return page.parent == null && page.isVisible;
  }

  public isActionMenuDisabled() {
    return this.currentPageIsSubpage();
  }

  public hasRetour(): boolean {
    return !this.hasMenu360 && this.crumbService.hasRetour();
  }

  public afficherBoutonRetourEnTete(): boolean {
    return this.hasRetour() || this.afficherRetourPersonnalise;
  }

  public traiterClicRetourEnTete(): void {
    if (this.afficherRetourPersonnalise) {
      this.retourPersonnalise.emit();
      return;
    }
    this.retour();
  }

  public retour() {
    this.crumbService.navigateToPageRetour();
  }

  private miseEnAvantDisabledByCurrentPage() {
    return this.pageLayoutService.pageSelected != null && this.pageLayoutService.pageSelected.hideMiseEnAvant;
  }

  isMiseEnAvantVisible() {
    return this.miseEnAvant && !this.miseEnAvantDisabledByCurrentPage();
  }
}
